import React, { useState } from 'react';
import MinimalLayout from '../../components/minimal/MinimalLayout';
import TodayCard from '../../components/minimal/TodayCard';
import IntelCard from '../../components/minimal/IntelCard';
import FootprintBar from '../../components/minimal/FootprintBar';
import SidePanel from '../../components/minimal/SidePanel';
import AskSageAnything from '../../components/AskSageAnything';

const HealthMinimal = () => {
  const [detailPanel, setDetailPanel] = useState(null);
  const [showAgent, setShowAgent] = useState(false);

  // Intelligence Stack - 6 cards max
  const intelCards = [
    {
      id: 'risk-radar',
      title: 'Risk Radar',
      subtitle: 'No major health risks detected',
      value: 'Low',
      icon: '🛡️',
      content: (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 rounded-lg">
            <h4 className="font-medium text-green-900 mb-2">Overall Risk: Low</h4>
            <p className="text-sm text-green-700">Your current planetary positions show minimal health vulnerabilities.</p>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-stone-600">Heart Health</span>
              <span className="text-green-600 font-medium">Excellent</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-stone-600">Digestive System</span>
              <span className="text-amber-600 font-medium">Monitor</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-stone-600">Mental Wellness</span>
              <span className="text-green-600 font-medium">Strong</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'organ-energy',
      title: 'Organ Energy Map',
      subtitle: 'Mars affects digestive system',
      value: '7.5/10',
      icon: '🫀',
      content: (
        <div className="space-y-3">
          <div className="p-3 bg-stone-50 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-stone-900">Heart</span>
              <span className="text-sm font-bold text-green-600">9.2/10</span>
            </div>
            <div className="w-full bg-stone-200 rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full" style={{ width: '92%' }} />
            </div>
          </div>
          <div className="p-3 bg-stone-50 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-stone-900">Digestive</span>
              <span className="text-sm font-bold text-amber-600">6.8/10</span>
            </div>
            <div className="w-full bg-stone-200 rounded-full h-2">
              <div className="bg-amber-500 h-2 rounded-full" style={{ width: '68%' }} />
            </div>
          </div>
          <div className="p-3 bg-stone-50 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-stone-900">Nervous System</span>
              <span className="text-sm font-bold text-green-600">8.4/10</span>
            </div>
            <div className="w-full bg-stone-200 rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full" style={{ width: '84%' }} />
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'timeline',
      title: 'Health Timeline',
      subtitle: 'Next 3 key dates',
      value: '3 events',
      icon: '📅',
      content: (
        <div className="space-y-3">
          <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg">
            <div className="text-xs text-stone-600 mb-1">Dec 15, 2024</div>
            <div className="font-medium text-stone-900 mb-1">Peak Energy Window</div>
            <p className="text-xs text-stone-700">Jupiter transit brings vitality boost. Schedule important activities.</p>
          </div>
          <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg">
            <div className="text-xs text-stone-600 mb-1">Jan 8, 2025</div>
            <div className="font-medium text-stone-900 mb-1">Digestive Care Phase</div>
            <p className="text-xs text-stone-700">Mars aspect. Avoid heavy foods, focus on gut health.</p>
          </div>
          <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg">
            <div className="text-xs text-stone-600 mb-1">Feb 20, 2025</div>
            <div className="font-medium text-stone-900 mb-1">Mental Wellness Peak</div>
            <p className="text-xs text-stone-700">Moon phase optimal. Perfect for meditation and stress relief.</p>
          </div>
        </div>
      ),
    },
    {
      id: 'remedies',
      title: 'Active Remedies',
      subtitle: 'Turmeric milk before sleep',
      value: '2 active',
      icon: '🌿',
      content: (
        <div className="space-y-3">
          <div className="p-4 border-l-4 border-green-500 bg-green-50 rounded-r-lg">
            <h4 className="font-medium text-stone-900 mb-2">Daily Remedy</h4>
            <p className="text-sm text-stone-700 mb-2">Turmeric milk before bed (9-10 PM)</p>
            <p className="text-xs text-stone-600">Reduces inflammation, supports immunity. Continue for 40 days.</p>
          </div>
          <div className="p-4 border-l-4 border-blue-500 bg-blue-50 rounded-r-lg">
            <h4 className="font-medium text-stone-900 mb-2">Breathwork</h4>
            <p className="text-sm text-stone-700 mb-2">Pranayama 10 minutes (6-7 AM)</p>
            <p className="text-xs text-stone-600">Strengthens nervous system, improves lung capacity.</p>
          </div>\n        </div>
      ),
    },
    {
      id: 'dasha',
      title: 'Current Dasha Impact',
      subtitle: 'Venus dasha supports vitality',
      value: 'Favorable',
      icon: '🪐',
      content: (
        <div className="space-y-4">
          <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
            <h4 className="font-medium text-stone-900 mb-2">Venus Mahadasha</h4>
            <p className="text-sm text-stone-700 mb-3">Period: 2023-2043 (20 years)</p>
            <div className="space-y-2 text-xs text-stone-600">
              <p>✓ Supports physical attractiveness and energy</p>
              <p>✓ Good for skin health and appearance</p>
              <p>✓ Enhances immunity and vitality</p>
              <p>⚠ Watch for overindulgence in food/drink</p>
            </div>
          </div>
          <div className="p-4 bg-stone-50 rounded-lg">
            <h4 className="font-medium text-stone-900 mb-2">Mercury Antardasha</h4>
            <p className="text-sm text-stone-700">Current sub-period till March 2025</p>
            <p className="text-xs text-stone-600 mt-2">Mental health strong, nervous system active.</p>
          </div>
        </div>
      ),
    },
    {
      id: 'body-clock',
      title: 'Body Clock Today',
      subtitle: 'Best times for activities',
      value: '24h map',
      icon: '⏰',
      content: (
        <div className="space-y-3">
          <div className="p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg">
            <div className="text-xs font-medium text-stone-900 mb-1">6:00 - 9:00 AM</div>
            <p className="text-sm text-stone-700">🏃 Exercise, Yoga, Meditation</p>
            <p className="text-xs text-stone-600 mt-1">Peak physical energy, high vitality</p>
          </div>
          <div className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg">
            <div className="text-xs font-medium text-stone-900 mb-1">12:00 - 2:00 PM</div>
            <p className="text-sm text-stone-700">🍽️ Main Meal Window</p>
            <p className="text-xs text-stone-600 mt-1">Digestive fire strongest</p>
          </div>
          <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
            <div className="text-xs font-medium text-stone-900 mb-1">4:00 - 6:00 PM</div>
            <p className="text-sm text-stone-700">💪 Strength Training</p>
            <p className="text-xs text-stone-600 mt-1">Muscle building optimal</p>
          </div>
          <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
            <div className="text-xs font-medium text-stone-900 mb-1">9:00 - 10:00 PM</div>
            <p className="text-sm text-stone-700">😴 Sleep Preparation</p>
            <p className="text-xs text-stone-600 mt-1">Wind down, avoid screens</p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <MinimalLayout
        title="Health"
        notificationCount={2}
        storyContent={
          <div className="space-y-4">
            <div className="aspect-video bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white">
              <div className="text-center">
                <div className="text-4xl mb-2">🎬</div>
                <p className="text-sm">Your Health Story Video</p>
                <p className="text-xs opacity-75 mt-1">Coming soon</p>
              </div>
            </div>
            <div className="prose prose-sm">
              <h3>Your Health Journey</h3>
              <p>A personalized video story about your health patterns, risks, and opportunities based on your birth chart...</p>
            </div>
          </div>
        }
        dashboardContent={
          <div className="space-y-4">
            <h3 className="font-serif text-xl font-medium">Full Health Analytics</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">83</div>
                <div className="text-xs text-stone-600 mt-1">Overall Score</div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">28</div>
                <div className="text-xs text-stone-600 mt-1">Days Streak</div>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-600">92%</div>
                <div className="text-xs text-stone-600 mt-1">Vitality</div>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-amber-600">7.2h</div>
                <div className="text-xs text-stone-600 mt-1">Avg Sleep</div>
              </div>
            </div>
          </div>
        }
        timelineContent={
          <div className="space-y-4">
            <h3 className="font-serif text-xl font-medium">Health Timeline</h3>
            <div className="space-y-3">
              {[2024, 2025, 2026, 2027].map(year => (
                <div key={year} className="p-4 bg-stone-50 rounded-lg">
                  <div className="font-medium text-stone-900 mb-2">{year}</div>
                  <p className="text-sm text-stone-600">Key health events and planetary influences...</p>
                </div>
              ))}
            </div>
          </div>
        }
      >
        {/* Section A: Today Card */}
        <TodayCard
          summary="Your vitality is stable today"
          score="8.3"
          icon="💚"
          gradient="from-green-50 to-emerald-50"
        />

        {/* Ask Agent Button */}
        <button
          onClick={() => setShowAgent(true)}
          className="w-full bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white py-3 px-4 rounded-lg font-medium transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
        >
          <span>💬</span>
          <span>Ask Vaidya Anything</span>
          <span>🏥</span>
        </button>

        {/* Section B: Intel Stack (6 cards max) */}
        <div className="space-y-3">
          {intelCards.map((card) => (
            <IntelCard
              key={card.id}
              title={card.title}
              subtitle={card.subtitle}
              value={card.value}
              icon={card.icon}
              onClick={() => setDetailPanel(card)}
            />
          ))}
        </div>

        {/* Section C: Footprint Bar */}
        <FootprintBar
          past="Childhood illnesses"
          present="Strong vitality phase"
          future="Longevity peak 2030"
        />
      </MinimalLayout>

      {/* Detail Panel */}
      <SidePanel
        isOpen={!!detailPanel}
        onClose={() => setDetailPanel(null)}
        title={detailPanel?.title || ''}
        position="right"
      >
        {detailPanel?.content}
      </SidePanel>

      {/* AI Agent Chat */}
      {showAgent && (
        <AskSageAnything 
          domainId="health"
          onClose={() => setShowAgent(false)}
        />
      )}
    </>
  );
};

export default HealthMinimal;
